import React, { ChangeEvent, useState, useEffect, useContext } from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Autocomplete,
  Alert,
  Box,
  Stack
} from "@mui/material";
import API from "api";
import Loader from "components/shared/Loader/Loader";
import Button from "components/shared/Button/Button";
import Session from "helpers/session";
import { useTranslation } from "react-i18next";
import { AddEncounterEvent } from "helpers/variables";
import { GlobalContext } from "context/Provider";
import EncounterTimer from "components/shared/EncounterTimer/EncounterTimer";

interface deviceInterface {
  serialnumber: string;
  user: string;
  type: string;
  assigned: boolean;
  orderDate: boolean;
  lot: string;
}

export default function Devices() {
  const memberID = window.location.pathname.split("/").pop();
  const { encounterDispatch, encounters, member } = useContext(GlobalContext);
  const [value, setValue] = useState<string>();
  const [devices, setDevices] = useState<deviceInterface[]>([]);
  const [waiting, setWaiting] = useState<boolean>(true);
  const [ImeI, setImeI] = useState<string>("");
  const [isValidImeI, setIsValidImeI] = useState(true);
  const [alertMessage, setAlertMessage] = useState<string>();
  const [availableDevices, setAvailableDevices] = useState([]);
  const [showSuccess, setSuccess] = useState<string>("");
  const [isDisabled, setChanged] = useState<boolean>(true);
  const { t } = useTranslation();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setSuccess("");
    setChanged(false);
  };

  const handleSelection = (event: any, value: any) => {
    console.log(value);
    setImeI(value);
    setIsValidImeI(checkImeI());
    setSuccess("");
    setChanged(false);
  };

  const helperTextRender = () => {
    return isValidImeI ? "" : t("s-p-devices-message-1");
  };

  const checkImeI = () => {
    return ImeI.length > 8 || isNaN(parseInt(ImeI)) ? true : false;
  };

  const getDeviceList = () => {
    API.kore.devices
      .getAssignedInventory(
        Session.crackCookieToken("coordinator"),
        "cellular",
        memberID || ""
      )
      .then((D) => {
        console.log(D);
        setWaiting(false);
        if (D) {
          setDevices(D.items);
        } else {
          setDevices([]);
        }
      })
      .catch((deviceErr) => console.log(deviceErr));
    API.kore.devices
      .getUnassignedInventory(
        Session.crackCookieToken("coordinator"),
        "cellular"
      )
      .then((deviceListRet) => {
        // console.log(deviceListRet);
        const deviceList = deviceListRet.items.map(
          (device: deviceInterface) => {
            return device.serialnumber.toUpperCase();
          }
        );
        setAvailableDevices(deviceList);
      })
      .catch((deviceErr) => console.log(deviceErr));
  };

  const renderList = () => {
    if (devices) {
      return devices.map((device) => {
        return (
          <FormControlLabel
            key={device.serialnumber + "7"}
            value={device.serialnumber}
            control={<Radio color="default" />}
            label={device ? device.serialnumber.toUpperCase() : ""}
          />
        );
      });
    } else {
      return <Loader />;
    }
  };

  const submissionClicked = () => {
    console.log(value);
    console.log(ImeI);
    setSuccess("success");
    setChanged(true);
    if (ImeI && value) {
      // call for both addition and removal of device
      if (
        window.confirm(
          `${t("s-p-devices-message-2")} ${ImeI} ${t(
            "s-p-devices-message-3"
          )} ${value}?`
        )
      ) {
        API.kore.devices
          .addCellDevice(
            Session.crackCookieToken("coordinator"),
            ImeI.toLowerCase(),
            memberID || ""
          )
          .then((cellularRes) => {
            if (cellularRes.request.status === 409) {
              const { assignee, portal } = JSON.parse(
                cellularRes.response.data.response.text
              );
              setAlertMessage(
                `${t("s-p-devices-message-4")}${assignee}${t(
                  "s-p-devices-message-5"
                )}${portal}${t("s-p-devices-message-6")}`
              );
            } else if (cellularRes.status === 500) {
              setAlertMessage(t("s-p-devices-message-7"));
            } else {
              setImeI("");
              setValue("");
              setWaiting(false);
            }
          });
        API.kore.devices.removeCellDevice(
          Session.crackCookieToken("coordinator"),
          value.toLowerCase(),
          memberID || ""
        );
      }
    } else if (ImeI) {
      if (window.confirm(`${t("s-p-devices-message-8")} ${ImeI}?`)) {
        API.kore.devices
          .addCellDevice(
            Session.crackCookieToken("coordinator"),
            ImeI.toLowerCase(),
            memberID || ""
          )
          .then((cellularRes) => {
            console.log(cellularRes);
            if (cellularRes.request.status === 409) {
              const { assignee, portal } = JSON.parse(
                cellularRes.response.data.response.text
              );
              setAlertMessage(
                `${t("s-p-devices-message-9")} ${assignee} ${t(
                  "s-p-devices-message-10"
                )} ${portal} ${t("s-p-devices-message-11")}`
              );
            } else if (cellularRes.status === 500) {
              setAlertMessage(t(`s-p-devices-message-12`));
            } else {
              setImeI("");
              setWaiting(false);
            }
          })
          .catch((err) => console.log("Something went device wrong"));
      }
    } else if (value) {
      // call for removal of device
      if (window.confirm(`${t("s-p-devices-message-13")} ${value}?`)) {
        API.kore.devices.removeCellDevice(
          Session.crackCookieToken("coordinator"),
          value.toLowerCase(),
          memberID || ""
        );
        setValue("");
        setWaiting(false);
      }
    } else {
      window.confirm(t("s-p-devices-message-14"));
    }
    const encounterEvent = AddEncounterEvent(
      "profile change",
      `Device configuration updated to ${ImeI}`
    );
    Session.updateSessionEncounter(
      window.sessionStorage.getItem("encounter") || "",
      encounterEvent,
      member?.secondsCounter.toString() || ""
    );
    setTimeout(() => {
      getDeviceList();
    }, 500);
  };

  useEffect(() => {
    window.scroll({
      top: 172,
      left: 0,
      behavior: "smooth"
    });
    getDeviceList();
  }, []);

  return (
    <Stack
      sx={{
        overflow: "scroll",
        position: "fixed",
        top: 290,
        bottom: 68,
        left: 160,
        right: 200,
        paddingBottom: 5,
        paddingLeft: 25,
        paddingRight: 5
      }}
    >
      <EncounterTimer />
      <Stack sx={{ paddingLeft: 10, paddingRight: 4 }} spacing={3}>
        <h1 style={{ margin: 0 }}>{t("u-connected-cellular-devices")}</h1>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t("u-device-by-imei")}</FormLabel>
          {alertMessage ? (
            <h3
              style={{
                fontFamily: "helvetica neue",
                color: "white",
                backgroundColor: "red",
                borderRadius: "3px",
                padding: "10px 100px"
              }}
            >
              {alertMessage}
            </h3>
          ) : (
            ""
          )}
          <RadioGroup
            aria-label="gender"
            name="device"
            value={value}
            onChange={handleChange}
          >
            {waiting ? (
              <Loader />
            ) : devices.length > 0 ? (
              renderList()
            ) : (
              <div>
                <p
                  style={{
                    color: "red",
                    border: "2px solid red",
                    borderRadius: "6px",
                    padding: "6px"
                  }}
                >
                  {t("s-no-cellulare-devices")}
                </p>
              </div>
            )}
          </RadioGroup>
        </FormControl>
        <Autocomplete
          disablePortal
          id="combo-box-demo"
          //TODO: choose one or the other
          // renderInput={(params)=> <TextField {...params} sx={{ width: 190}} id="imei" variant="outlined" label={'Add Device'} helperText={helperTextRender()} error={!isValidImeI} fullWidth={false} value={ImeI}/>}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ width: 190 }}
              id="imei"
              variant="outlined"
              label={t("u-add-device")}
              helperText={helperTextRender()}
              error={!isValidImeI}
              onChange={(e: any) => {
                setImeI(e.target.value);
                setIsValidImeI(checkImeI());
              }}
              fullWidth={false}
              value={ImeI}
            />
          )}
          options={availableDevices}
          onChange={handleSelection}
        />
        <Button
          variant={"primary"}
          onClick={submissionClicked}
          disabled={isDisabled}
        >
          {ImeI ? "Add Device" : "Remove Device"}
        </Button>
        {showSuccess === "success" ? (
          <Alert severity="success">
            Your configuration changes have been saved
          </Alert>
        ) : (
          ""
        )}
      </Stack>
    </Stack>
  );
}
